import {
  AssetModel,
  AssetSelfTestCategorySummaryModel,
  useGetAssetSelfTestSummaryQuery,
} from '@customer/store/apis/assetsApi';
import styles from './AssetPanelSelfTestSummary.module.scss';
import { useTranslation } from 'react-i18next';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { useAppSelector } from '@customer/store/hooks';
import { useMemo, useState } from 'react';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { ColDef } from 'ag-grid-community';
import CasAccordion from '@customer/components/accordions/accordion/CasAccordion';
import AccordionHeaderFaults from '@customer/components/accordions/header-faults/AccordionHeaderFaults';
import { AccordionStatus } from '@customer/components/accordions/types';
import useControlledAccordions from '@customer/components/accordions/useControlledAccordions';
import { FaultLevel, FaultLevelKey, faultLevelKeys } from '@customer/store/slices/globalSlice';
import AssetInfo, { AssetInfoRow } from '@customer/components/asset-data/asset-info/AssetInfo';
import { AccordionTable } from '@customer/components/accordions/table/AccordionTable';
import { calcPercentage } from '@cas/utils/numberHelpers';
import {
  colDefDuration,
  columnDefSelfTestSummaryActiveFaults,
  columnDefSelfTestSummaryCategory,
  columnDefSelfTestSummaryTotalFaults,
} from '@customer/components/tables/grid/columnDefs';

const getRowId = (data: AssetSelfTestCategorySummaryModel) => data.cat.toString();

export interface AssetPanelSelfTestSummaryProps {
  asset: AssetModel;
}

export function AssetPanelSelfTestSummary({ asset }: AssetPanelSelfTestSummaryProps) {
  const { t } = useTranslation();
  const { globalTimeRange, showDurationFromSeconds } = useLocaleFormatter();

  const { timeRange, sidePanelDefaultAccordion } = useAppSelector(state => state.global);

  const { currentData: assetData } = useGetAssetSelfTestSummaryQuery(
    {
      assetId: asset.id.toString(),
      query: { timeRange },
    },
    { refetchOnMountOrArgChange: 60 },
  );

  const percentages = useMemo<number[]>(() => {
    if (assetData) {
      return faultLevelKeys.map((faultLevelKey: FaultLevelKey) => {
        const faultLevelIndex = Number(FaultLevel[faultLevelKey]) - 1;
        return calcPercentage(assetData.lvls[faultLevelIndex].dur, assetData.ign);
      });
    } else {
      return Array(faultLevelKeys.length).fill(0);
    }
  }, [assetData]);

  const { getControlledProps } = useControlledAccordions(sidePanelDefaultAccordion);

  const [columnDefs] = useState<ColDef<AssetSelfTestCategorySummaryModel>[]>(() => [
    { ...columnDefSelfTestSummaryCategory(t) },
    { ...columnDefSelfTestSummaryActiveFaults(t) },
    { ...columnDefSelfTestSummaryTotalFaults(t) },
    { ...colDefDuration(t('TotalDuration')) },
  ]);

  const infoRows: AssetInfoRow[] = [
    {
      label: t('SelfTestIgnitionOnTime'),
      value: showDurationFromSeconds(assetData?.ign ?? 0),
      emphasis: true,
    },
    {
      label: t('ShowingDataFrom'),
      value: globalTimeRange,
    },
  ];

  return (
    <div className={styles.container}>
      <AssetInfo rows={infoRows} />

      <div className={styles.accordions}>
        {assetData &&
          faultLevelKeys.map((faultLevelKey: FaultLevelKey) => {
            const faultLevel = FaultLevel[faultLevelKey];
            const faultLevelIndex = Number(faultLevel) - 1;

            return (
              <CasAccordion
                key={faultLevel}
                status={AccordionStatus.Neutral}
                controlled={getControlledProps(faultLevel)}
                header={
                  <AccordionHeaderFaults
                    level={faultLevel}
                    percentage={percentages[faultLevelIndex]}
                    totalCount={assetData.lvls[faultLevelIndex].tot}
                  />
                }
              >
                <AccordionTable hasRows={!!assetData.lvls[faultLevelIndex].cats.length}>
                  <GridControlled<AssetSelfTestCategorySummaryModel>
                    autoheight={true}
                    sliceKey={`AssetPanelSelfTestLevel${faultLevel}`}
                    rowData={assetData.lvls[faultLevelIndex].cats}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                  />
                </AccordionTable>
              </CasAccordion>
            );
          })}
      </div>
    </div>
  );
}

export default AssetPanelSelfTestSummary;
